import { Injectable } from '@angular/core';
import { Alert, AlertType } from '@obo-main/services/common/alert/alert.models';
import { convertFrom } from '@obo-main/utils/convert-units';
import { Subject, timer } from 'rxjs';

@Injectable()
export class AlertService {
    public onNewAlert: Subject<Alert> = new Subject<Alert>();
    public onAlertDispose: Subject<Alert> = new Subject<Alert>();
    public onAlertsReset: Subject<void> = new Subject<void>();
    private recentAlerts: RecentAlert[] = [];
    private readonly deduplicationWindow: number = 5000;
    private readonly maxCachedAlerts: number = 10;

    constructor() {}

    /**
     * disposes all active Alerts
     */
    public resetAlerts() {
        this.onAlertsReset.next();
        this.recentAlerts = [];
    }

    /**
     * creates a successalert
     * @param text Alert's text
     * @param disposeAfter time, after which the Alert shall disappear, default 5sec
     * @param title Alert's title
     */
    public success(text: string, disposeAfter?: number, title?: string): void {
        this.addAlert(text, AlertType.Success, disposeAfter || 5, title);
    }

    /**
     * creates a warningalert
     * @param text ALert's text
     * @param disposeAfter time, after which the Alert shall disappear, default 20sec
     * @param title Alert's title
     */
    public danger(text: string, disposeAfter?: number, title?: string): void {
        this.addAlert(text, AlertType.Error, disposeAfter || 20, title);
    }

    /**
     * creates a infoalert
     * @param text ALert's text
     * @param disposeAfter time, after which the Alert shall disappear, default 5sec
     * @param title Alert's title
     */
    public info(text: string, disposeAfter?: number, title?: string): void {
        this.addAlert(text, AlertType.Info, disposeAfter || 5, title);
    }

    public warning(text: string, disposeAfter?: number, title?: string): void {
        this.addAlert(text, AlertType.Warning, disposeAfter || 20, title);
    }

    private addAlert(text: string, type: AlertType, disposeAfter: number, title?: string): void {
        // Check if this is a duplicate alert
        if (this.isDuplicateAlert(text, type)) {
            console.info('Suppressed duplicate alert:', text);
            return;
        }

        // Track this alert to prevent duplicates
        this.trackAlert(text, type);

        // Convert seconds to milliseconds
        disposeAfter = convertFrom(disposeAfter, 's').to('ms');

        let newAlert: Alert = {
            text: text,
            title: title,
            type: type
        };

        this.onNewAlert.next(newAlert); // emits the new alert
        timer(disposeAfter).subscribe(() => {
            // creates a timer
            this.onAlertDispose.next(newAlert); // disposes the newly created alert after timer ellapsed
        });
    }

    private isDuplicateAlert(text: string, type: AlertType): boolean {
        const now = Date.now();

        // Clean up old alerts first
        this.cleanupOldAlerts();

        // Check if this alert text+type combo exists in our recent alerts
        return this.recentAlerts.some(
            (alert) => alert.text === text && alert.type === type && now - alert.timestamp < this.deduplicationWindow
        );
    }

    private trackAlert(text: string, type: AlertType): void {
        this.recentAlerts.push({
            text,
            type,
            timestamp: Date.now()
        });

        if (this.recentAlerts.length > this.maxCachedAlerts) {
            this.recentAlerts.shift();
        }
    }

    private cleanupOldAlerts(): void {
        const now = Date.now();
        this.recentAlerts = this.recentAlerts.filter((alert) => now - alert.timestamp < this.deduplicationWindow);
    }
}

interface RecentAlert {
    text: string;
    type: AlertType;
    timestamp: number;
}
